import React, { useEffect, useCallback, useRef } from "react";
import { compose } from "recompose";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/styles";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { getLocationFromMap, hasBoundsChanged } from './helpers';
import _ from 'lodash';
import MapOverlayOptions from './MapOverlayOptions';
import { defaultMapStyles, blackGoldStyles } from '../explore/gmap/mapStyles';
import MapOverlayShapeInfoBox from './MapOverlayShapeInfoBox';
import AssumptionsShapeManager from './AssumptionsShapeManager';
import AssumptionsGrid from './AssumptionsGrid';
import ContextMenu from 'devextreme-react/context-menu';
import { Stack } from 'components/layout';
import PossibleShapesFilter from './PossibleShapesFilter';
import {
  setMapLocation,
  setMapType,
  fetchCustomShapes,
  setSelectedShapes,
} from 'store/actions';
import { googleMapsApiKey } from "index";

const scriptLibraries = ['geometry', 'drawing', 'places'];

const AssumptionsMap = ({
  classes,
  gridRef,
  selectedGroup,
  selectedGroupIsOpen,
  showFieldValueGrid,
  selectedCustomShape,
  creatingNewShape,
  shapesGeoType,
  shapes,
  bounds,
  zoom,
  center,
  forceFetchAfterFitBounds,
  setSelectedShapes,
  setMapLocation,
  setMapType,
}) => {
  // == Constants =============================================

  const canShowGrid =
    selectedGroup && selectedGroupIsOpen && selectedGroup.shapes.length > 0;

  const contextMenuItems = [
    {
      text: 'Select All',
    },
  ];

  // == useJsApiLoader ========================================

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey,
    libraries: scriptLibraries,
  });

  // == useRef ================================================

  const mapRef = useRef(null);
  const initialized = useRef(false);
  const prevZoom = useRef(null);

  // == useEffect =============================================

  useEffect(() => {
    if (!mapRef?.current || !bounds?.length) return;

    const mapLocation = getLocationFromMap(mapRef.current);
    if (hasBoundsChanged(mapLocation, bounds) || forceFetchAfterFitBounds) {
      mapRef.current.fitBounds({
        south: bounds[0],
        north: bounds[1],
        west: bounds[2],
        east: bounds[3],
      });
    }
  }, [bounds]);

  useEffect(() => {
    if (!mapRef?.current || !selectedCustomShape) return;

    if (selectedCustomShape.boundingBox) {
    const boundingBox = selectedCustomShape.boundingBox;
      mapRef.current.fitBounds({
        north: boundingBox.north,
        east: boundingBox.east,
        south: boundingBox.south,
        west: boundingBox.west,
      });
    }
  }, [selectedCustomShape]);

  // == useCallback =========================================

  const getMapStyle = useCallback(() => {
    const defaultStyle = new window.google.maps.StyledMapType(
      defaultMapStyles,
      { name: 'Default' }
    );
    const styledMapType = new window.google.maps.StyledMapType(
      blackGoldStyles,
      { name: 'Spooky' }
    );
    return [
      {
        id: 'default',
        style: defaultStyle,
      },
      {
        id: 'halloween_satellite',
        style: styledMapType,
      },
    ];
  }, []);

  const onLoad = useCallback(
    (map) => {
      mapRef.current = map;
      const mapStyles = getMapStyle();
      mapStyles.forEach((el) => {
        mapRef.current.mapTypes.set(el.id, el.style);
      });
      mapRef.current.setMapTypeId('default');
    },
    [getMapStyle]
  );
  if (loadError) {
    console.log('error loading map');
    return null;
  }

  // == Event Handlers =====================================

  const onZoomChanged = () => {
    if (!mapRef.current) return;
    const newZoom = mapRef.current.getZoom();
    if (prevZoom.current == null) {
      prevZoom.current = newZoom;
    } else if (prevZoom.current !== newZoom) {
      prevZoom.current = newZoom;
    }
  };

  const onBoundsChangedDebounced = _.debounce(onBoundsChanged, 1000);
  function onBoundsChanged() {
    if (!mapRef?.current) return;
    const newLocation = getLocationFromMap(mapRef.current);
    if (!initialized.current) {
      initialized.current = true;
      if (zoom == null || center == null) {
        mapRef.current.fitBounds({
          south: bounds[0],
          north: bounds[1],
          west: bounds[2],
          east: bounds[3],
        });
      }
    } else if (
      hasBoundsChanged(newLocation, bounds) ||
      forceFetchAfterFitBounds
    ) {
      setMapLocation(newLocation, false);
    }
  }

  const handleMapTypeChange = () => {
    if (!mapRef.current) return;
    setMapType(mapRef.current.getMapTypeId());
  };

  const handleItemClick = (e) => {
    if (e.itemData.text === 'Select All' && shapes.length > 0) {
      const allShapes = shapes.map((s) => {
        return { id: s.id, level: shapesGeoType };
      });

      setSelectedShapes(allShapes);
    }
  };

  const handleContextMenuShowing = (e) => {
    if (shapes.length === 0 || !creatingNewShape) {
      e.cancel = true;
    }
  };

  // == Components ========================================

  if (!isLoaded) {
    return <div>Loading Map</div>;
  }

  return (
    <div className={classes.container}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        onLoad={onLoad}
        center={center}
        zoom={zoom}
        clickableIcons={false}
        onBoundsChanged={onBoundsChangedDebounced}
        onMapTypeIdChanged={handleMapTypeChange}
        onZoomChanged={onZoomChanged}
        options={{
          gestureHandling: 'greedy',
          cameraControl: false,
          zoomControl: true,
          mapTypeControlOptions: {
            style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
            position: window.google.maps.ControlPosition.TOP_RIGHT,
            mapTypeIds: [
              'default',
              'roadmap',
              'terrain',
              'hybrid',
              'satellite',
              'halloween_satellite',
            ],
          },
          fullscreenControlOptions: {
            position: window.google.maps.ControlPosition.BOTTOM_LEFT,
          },
          minZoom: 4,
          restriction: {
            latLngBounds: {
              west: -179.9999999,
              east: 179.9999999,
              north: 90,
              south: -90,
            },
            strictBounds: true,
          },
        }}
      >
        <div className={classes.shapeSelectorContainer}>
          <Stack horizontal>
            <MapOverlayOptions />
            <PossibleShapesFilter />
          </Stack>
        </div>

        <div className={classes.shapeInfoContainer}>
          <MapOverlayShapeInfoBox />
        </div>

        <AssumptionsShapeManager />
      </GoogleMap>
      <ContextMenu
        onShowing={handleContextMenuShowing}
        dataSource={contextMenuItems}
        onItemClick={handleItemClick}
      />
      {canShowGrid && showFieldValueGrid && <AssumptionsGrid gridRef={gridRef} />}
    </div>
  );
};

// == Styles ===============================================

const containerStyle = {
  flexGrow: 1,
  display: 'flex',
  position: 'relative',
  width: '100%',
};

const styles = {
  showAssumptionGroupContainer: {
    marginLeft: 10,
    cursor: 'pointer',
    padding: 10,
  },
  filtersContainer: {
    marginLeft: 10,
    padding: 10,
  },
  container: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexFlow: 'column wrap',
    overflow: 'hidden',
  },
  shapeSelectorContainer: {
    position: 'absolute',
    top: '10px',
    left: '10px',
  },
  shapeInfoContainer: {
    position: 'absolute',
    top: '65px',
    left: '10px',
  },
};

// == Connect ==============================================

const mapStateToProps = ({ assumptionMapState, assumptionGroupsState }) => ({
  bounds: assumptionMapState.bounds,
  zoom: assumptionMapState.zoom,
  center: assumptionMapState.center,
  shapesGeoType: assumptionMapState.shapesGeoType,
  shapes: assumptionMapState.shapes,
  forceFetchAfterFitBounds: assumptionMapState.forceFetchAfterFitBounds,
  selectedGroup: assumptionGroupsState.selectedGroup,
  selectedGroupIsOpen: assumptionGroupsState.selectedGroupIsOpen,
  showFieldValueGrid: assumptionGroupsState.showFieldValueGrid,
  selectedCustomShape: assumptionGroupsState.selectedCustomShape,
  creatingNewShape: assumptionGroupsState.creatingNewShape,
});

const mapDispatchToProps = {
  setMapLocation,
  setMapType,
  fetchCustomShapes,
  setSelectedShapes,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(AssumptionsMap);
